/**
 * Which document types a company may file to Lao eTax (การนำส่งตามประเภทเอกสาร)
 *
 * Whether a company bills its customers with TX or with RT is a fact about that
 * customer's source system, not about this program — one of them issues its tax
 * invoices as RT while its TX documents are the SAME sales again under an
 * accounting reference, so offering both pages would invite declaring the VAT
 * twice. It is therefore per-company configuration, read from Settings/laoEtax:
 *
 *   Settings/laoEtax.docTypes["<companyID>"] = [6, 4]   // this company: RT + CR
 *   Settings/laoEtax.defaultDocTypes         = [1, 4, 6] // everyone else
 *
 * Authoritative resolution lives in firebase/functions/src/libs/libLaoEnv.js
 * (resolveLaoDocTypes). This module decides only whether the UI OFFERS the
 * action: enqueueLaoEtaxIssue resolves the same list server-side and refuses
 * anything outside it BEFORE writing any status, so a stale tab or a direct
 * callable cannot file a doc type a company has turned off.
 *
 * Settings/laoEtax is world-readable (firestore.rules `match /Settings`), so the
 * client reads it directly rather than paying for a callable round-trip.
 */

export const LAO_DOC_TYPE_TX = 1; // ใบกำกับภาษี
export const LAO_DOC_TYPE_CR = 4; // ใบลดหนี้
export const LAO_DOC_TYPE_RT = 6; // ใบเสร็จรับเงิน/ใบกำกับภาษี

// Everything the Lao mapper can shape a payload for. Also the fallback when
// Settings carries no doc-type key, which keeps behaviour identical to before
// the allow-list existed until someone actually writes the setting.
// MUST match SUPPORTED_DOC_TYPES in functions/src/libs/libLaoEnv.js.
export const SUPPORTED_LAO_DOC_TYPES = [
  LAO_DOC_TYPE_TX,
  LAO_DOC_TYPE_CR,
  LAO_DOC_TYPE_RT,
];

// Unlike the backend, an unsupported entry is dropped rather than thrown on:
// a bad setting must fail loudly where documents are filed, not by breaking a
// table the user is only browsing. The backend still refuses the whole send.
function normalize(raw) {
  if (!Array.isArray(raw)) return null;
  const out = [];
  raw.forEach((v) => {
    const n = Number(v);
    if (SUPPORTED_LAO_DOC_TYPES.indexOf(n) !== -1 && out.indexOf(n) === -1) {
      out.push(n);
    }
  });
  return out;
}

/**
 * Resolve the allow-list for one company.
 *
 * Returns null when the setting could not be read. Callers treat null as "show
 * no Lao action": for a button that files real tax invoices, an unknown answer
 * has to mean off, and the backend would refuse the send anyway.
 *
 * @param {firebase.firestore.Firestore} db
 * @param {string} companyID
 * @returns {Promise<number[]|null>}
 */
export async function resolveAllowedLaoDocTypes(db, companyID) {
  try {
    const snap = await db.collection("Settings").doc("laoEtax").get();
    if (!snap.exists) return SUPPORTED_LAO_DOC_TYPES.slice();

    const data = snap.data() || {};
    const cid = (companyID || "").toString().trim();
    const map = data.docTypes;
    if (
      cid &&
      map &&
      typeof map === "object" &&
      Object.prototype.hasOwnProperty.call(map, cid)
    ) {
      const forCompany = normalize(map[cid]);
      if (forCompany) return forCompany;
    }

    const fallback = normalize(data.defaultDocTypes);
    if (fallback) return fallback;

    return SUPPORTED_LAO_DOC_TYPES.slice();
  } catch (e) {
    console.error("resolveAllowedLaoDocTypes: Settings/laoEtax read failed", e);
    return null;
  }
}

export default {
  SUPPORTED_LAO_DOC_TYPES,
  resolveAllowedLaoDocTypes,
};
